'use client'

/**
 * TermTooltip Component
 *
 * A specialized tooltip for lawn care terminology that:
 * - Automatically looks up definitions from the centralized glossary
 * - Shows dashed underline styling to indicate learnable terms
 * - Supports both hover (desktop) and tap (mobile) interactions
 * - Provides short definitions on hover, with optional expanded view
 *
 * Usage:
 *   <TermTooltip term="NPK">NPK ratio</TermTooltip>
 *   <TermTooltip term="pre-emergent">pre-emergent herbicide</TermTooltip>
 */

import * as React from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { getTermDefinition, TermDefinition } from '@/data/lawn-care-glossary'
import { cn } from '@/lib/utils'
import { Info } from 'lucide-react'

interface TermTooltipProps {
  /** The glossary term key to look up (e.g., "NPK", "pre-emergent") */
  term: string
  /** The text to display (can differ from term key) */
  children: React.ReactNode
  /** Whether to show the expanded definition */
  showLongDef?: boolean
  /** Custom short definition (overrides glossary) */
  customShortDef?: string
  /** Custom long definition (overrides glossary) */
  customLongDef?: string
  /** Additional CSS classes for the trigger span */
  className?: string
  /** Side preference for tooltip placement */
  side?: 'top' | 'right' | 'bottom' | 'left'
}

/**
 * Renders a term with a dashed underline and tooltip showing its definition.
 * Designed for first-time users who may be unfamiliar with lawn care terminology.
 */
export function TermTooltip({
  term,
  children,
  showLongDef = false,
  customShortDef,
  customLongDef,
  className,
  side = 'top',
}: TermTooltipProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const definition = getTermDefinition(term)

  // Use custom definitions if provided, otherwise fall back to glossary
  const shortDef = customShortDef || definition?.shortDef
  const longDef = customLongDef || definition?.longDef

  // If no definition found and no custom provided, just render children
  if (!shortDef) {
    return <>{children}</>
  }

  // Handle touch devices - toggle on tap
  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault()
    setIsOpen(!isOpen)
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip open={isOpen} onOpenChange={setIsOpen}>
        <TooltipTrigger asChild>
          <span
            className={cn(
              // Base styling with dashed underline
              'border-b border-dashed border-gray-400 cursor-help',
              // Ensure minimum touch target on mobile (44px)
              'inline-block min-h-[22px] py-0.5',
              // Hover state
              'hover:border-green-600 hover:text-green-700',
              // Focus state for accessibility
              'focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:ring-offset-1 rounded-sm',
              // Transition for smooth interactions
              'transition-colors duration-150',
              className
            )}
            onTouchStart={handleTouchStart}
            tabIndex={0}
            role="button"
            aria-label={`Learn about ${definition?.term || term}`}
          >
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          className="max-w-xs bg-white border-gray-200 shadow-lg"
        >
          <div className="space-y-1.5">
            {/* Term header with icon */}
            <div className="flex items-center gap-1.5 text-green-700 font-medium">
              <Info className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{definition?.term || term}</span>
            </div>

            {/* Short definition */}
            <p className="text-gray-700 text-sm leading-relaxed">
              {shortDef}
            </p>

            {/* Long definition (if requested and available) */}
            {showLongDef && longDef && (
              <p className="text-gray-600 text-xs leading-relaxed pt-1 border-t border-gray-100">
                {longDef}
              </p>
            )}

            {/* Category badge */}
            {definition?.category && (
              <div className="pt-1">
                <span className="inline-block text-[10px] uppercase tracking-wide text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">
                  {definition.category}
                </span>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/**
 * Inline variant for use within paragraphs where less visual prominence is needed.
 * Uses a subtler underline style.
 */
export function TermTooltipInline({
  term,
  children,
  className,
  ...props
}: TermTooltipProps) {
  return (
    <TermTooltip
      term={term}
      className={cn('border-gray-300 hover:border-gray-500', className)}
      {...props}
    >
      {children}
    </TermTooltip>
  )
}

/**
 * Helper component to wrap multiple terms in a sentence.
 * Automatically highlights and adds tooltips for recognized terms.
 *
 * Usage:
 *   <TermHighlighter text="Apply NPK fertilizer as a pre-emergent treatment" />
 */
interface TermHighlighterProps {
  text: string
  /** Terms to highlight (if not provided, checks all glossary terms) */
  terms?: string[]
  /** Show long definitions */
  showLongDef?: boolean
  className?: string
}

export function TermHighlighter({
  text,
  terms,
  showLongDef = false,
  className,
}: TermHighlighterProps) {
  // Build regex pattern from terms to highlight
  const termList = terms || [
    'NPK', 'nitrogen', 'phosphorus', 'potassium',
    'pre-emergent', 'post-emergent', 'selective herbicide',
    'slow-release', 'quick-release', 'fertilizer burn',
    'one-third rule', 'grass cycling', 'scalping',
    'deep watering', 'drought stress', 'evapotranspiration',
    'aeration', 'core aeration', 'soil compaction', 'thatch',
    'germination', 'overseeding', 'soil temperature', 'dormant seeding',
    'cool-season grass', 'warm-season grass', 'transition zone',
    'growing season', 'dormancy', 'soil pH'
  ]

  // Create case-insensitive pattern
  const pattern = new RegExp(
    `\\b(${termList.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`,
    'gi'
  )

  // Split text and wrap matching terms
  const parts = text.split(pattern)

  return (
    <span className={className}>
      {parts.map((part, index) => {
        // Check if this part matches a term (case-insensitive)
        const matchedTerm = termList.find(
          t => t.toLowerCase() === part.toLowerCase()
        )

        if (matchedTerm) {
          // Convert display term to glossary key format
          const glossaryKey = matchedTerm.toLowerCase().replace(/\s+/g, '-')
          return (
            <TermTooltip
              key={index}
              term={glossaryKey}
              showLongDef={showLongDef}
            >
              {part}
            </TermTooltip>
          )
        }

        return <React.Fragment key={index}>{part}</React.Fragment>
      })}
    </span>
  )
}

export default TermTooltip
